import { useCallback } from "react";
import {
  Box,
  Button,
  Grid,
  FormHelperText
} from "@mui/material";
import InstanceFilterRule from "./InstanceFilterRule";
import { useCustomSnackbar } from "../CustomSnackbar";
import { useQuery } from "@apollo/client";
import { GET_TEST_TEMPLATES, GET_TAGS } from "../../graphql/queries";
import { TestTemplateStatus } from "../../graphql/enums";
import { Waiting } from "../Waiting";
import { useAppContext } from "../../context/AppContext";
import { useTranslation } from "react-i18next";

const InstanceFilter = ({
  filter,
  setFilter,
  disabled = false,
  allowSelectTestTemplate = true,
  allowStatus=true,
  testTemplateId = '',
  defaultFilter = null
}) => {
  const { lsEnqueueSnackbar } = useCustomSnackbar();
  const { store } = useAppContext();
  const { t } = useTranslation();

  // Fetch test templates
  const {
    data: utData,
    loading: utLoading,
  } = useQuery(GET_TEST_TEMPLATES, {
    variables: {
      orgId: store.currentOrg?.org.id,
      templateStatus: [
        TestTemplateStatus.Active,
        TestTemplateStatus.Deprecated,
        TestTemplateStatus.Retired
      ],
    },
    onError: (e) => {
      lsEnqueueSnackbar(e.message, {
        variant: "error",
      });
    },
  });

  // Get the list of available tags
  const {
    loading: gtLoading,
    data: gtData
  } = useQuery(GET_TAGS, {
    variables: {
      orgId: store.currentOrg?.org.id
    },
    onError: (e) => {
      lsEnqueueSnackbar(e.message, {
        variant: "error",
      });
    }
  });

  // Define Validation Logic
  const isFilterRuleComplete = useCallback((rule) => {
    return (rule.values.dateValues && rule.values.dateValues.length) ||
      (rule.values.stringValues && rule.values.stringValues.length)
  }, []);

  const isFilterValid = filter.every(isFilterRuleComplete);

  const handleAddRule = () => {
    if (defaultFilter) {
      setFilter([...filter, defaultFilter]);
    } else {
      setFilter([...filter, { field: 'testTemplateId', operation: 'eq', values: [testTemplateId] }]);
    }
  };

  const handleDeleteRule = (index) => {
    setFilter(filter.filter((_, i) => i !== index));
  };

  const handleChangeRule = (index, newRule) => {
    setFilter(filter.map((rule, i) => (i === index ? newRule : rule)));
  };



  let pageResults;
  if (utLoading || gtLoading) {
    pageResults = <Waiting />;
  } else {
    pageResults = (
      <Box>
        <Grid container spacing={2}>
          {filter.map((rule, index) => (
            <Grid item xs={12} key={index}>
              <InstanceFilterRule
                key={index}
                filterRule={rule}
                index={index}
                ruleCount={filter.length}
                onChange={handleChangeRule}
                onDelete={handleDeleteRule}
                templates={utData.getTestTemplates}
                tags={gtData.getTags}
                allowSelectTestTemplate={allowSelectTestTemplate}
                allowStatus={allowStatus}
                testTemplateId={testTemplateId}
                disabled={disabled}
              />
            </Grid>
          ))}
          {!isFilterValid && !disabled && (
            <Grid item xs={12}>
              <FormHelperText error>{t("IncompleteFilterRules")}</FormHelperText>
            </Grid>
          )}
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddRule}
              disabled={disabled || !isFilterValid} // Disable if filter is incomplete
            >
              {t("AddFilter")}
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  }
  return pageResults;
};

export default InstanceFilter;